import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

// Renders a square 1:1 box that fills its parent's width, and exposes the
// observed pixel width to its render-prop child. Used to size the sensory
// radar to the parent column without forcing a fixed pixel value.
//
// `ResizeObserver` keeps the SVG re-rendered crisply across viewport /
// sidebar density changes. The initial measurement seeds via
// `getBoundingClientRect()` so the first paint isn't an empty box waiting
// for the observer to fire.
type RadarFillProps = {
  children: (size: number) => ReactNode;
};

export function RadarFill({ children }: RadarFillProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState(0);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    if (rect.width > 0) setSize(Math.round(rect.width));
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = Math.round(e.contentRect.width);
        if (w > 0) setSize(w);
      }
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        width: "100%",
        aspectRatio: "1 / 1",
        position: "relative",
      }}
    >
      {size > 0 && children(size)}
    </div>
  );
}
